/*
 * Телевизор на низком стеллаже: на столешнице на своих ножках (55″, перед
 * экраном — саундбар) или на стене над изделием (65″). Это декор для
 * витрины и 3D: в смету и техкарту он не входит.
 */
import type { PlannerProject, WardrobeModule } from "../types";
import { ceilingHeightMm } from "./moduleRules";

export type ShelvingTvMount = "stand" | "wall";

export const SHELVING_TV_MOUNTS: readonly ShelvingTvMount[] = ["stand", "wall"];

export const SHELVING_TV_MODELS = {
  stand: { file: "tv_55_stand_on.glb", title: "55″ на ножках", dimensionsMm: [1230, 782, 268] as const },
  wall: { file: "tv_65_wall_off.glb", title: "65″ на стене", dimensionsMm: [1451, 828, 56] as const }
} as const;

export const SHELVING_SOUNDBAR = { file: "soundbar.glb", dimensionsMm: [900, 67, 100] as const };

/** Телевизор ставится только на низкое изделие: тумбу, буфет, комод. */
export const SHELVING_TV_MAX_UNIT_HEIGHT_MM = 1200;
/** Ножки телевизора 55″ разнесены на 996 мм: столешница должна быть шире. */
export const SHELVING_TV_STAND_MIN_WIDTH_MM = 1040;
/** Опоры ножек 268 мм в глубину и по 10 мм запаса спереди и сзади. */
export const SHELVING_TV_STAND_MIN_DEPTH_MM = 290;
export const SHELVING_TV_WALL_MIN_WIDTH_MM = 800;
/** От верха изделия до низа телевизора на стене. */
export const SHELVING_TV_WALL_GAP_MM = 150;
const CEILING_CLEARANCE_MM = 60;

export const normalizeShelvingTv = (value: unknown): ShelvingTvMount | undefined =>
  value === "stand" || value === "wall" ? value : undefined;

/** Почему этот вариант телевизора недоступен; null — можно ставить. */
export function shelvingTvProblem(
  project: Pick<PlannerProject, "modules" | "room">,
  module: WardrobeModule,
  mount: ShelvingTvMount
): string | null {
  if (module.type !== "shelving" || module.stackedOnId) return "Телевизор ставится на основное изделие";
  if (project.modules.some((candidate) => candidate.stackedOnId === module.id)) {
    return "Сверху стоит антресоль";
  }
  if (module.height > SHELVING_TV_MAX_UNIT_HEIGHT_MM) {
    return `Изделие выше ${SHELVING_TV_MAX_UNIT_HEIGHT_MM} мм`;
  }
  if (mount === "stand") {
    if (module.width < SHELVING_TV_STAND_MIN_WIDTH_MM) return `Нужна ширина от ${SHELVING_TV_STAND_MIN_WIDTH_MM} мм`;
    if (module.depth < SHELVING_TV_STAND_MIN_DEPTH_MM) return `Нужна глубина от ${SHELVING_TV_STAND_MIN_DEPTH_MM} мм`;
    return null;
  }
  if (module.width < SHELVING_TV_WALL_MIN_WIDTH_MM) return `Нужна ширина от ${SHELVING_TV_WALL_MIN_WIDTH_MM} мм`;
  const top = module.height + SHELVING_TV_WALL_GAP_MM + SHELVING_TV_MODELS.wall.dimensionsMm[1];
  if (top + CEILING_CLEARANCE_MM > ceilingHeightMm(project as PlannerProject)) return "Не помещается до потолка";
  return null;
}

export interface ShelvingTvPlacement {
  mount: ShelvingTvMount;
  /** В координатах изделия: 0 — центр по ширине, пол, передняя кромка; назад — минус. */
  bottomMm: number;
  centerZmm: number;
  /** Саундбар перед экраном, между ножками телевизора. */
  soundbar: { bottomMm: number; centerZmm: number } | null;
}

export function shelvingTvPlacement(
  project: Pick<PlannerProject, "modules" | "room">,
  module: WardrobeModule
): ShelvingTvPlacement | null {
  // Телевизор — предмет интерьера, а не товар: выбирать его в меню не нужно
  // (приёмка 05.10, ch-05, sg-11). На низкое широкое изделие он встаёт сам,
  // на ножках; прячется вместе с остальными предметами кнопкой «Предметы».
  const mount = normalizeShelvingTv(module.shelvingTv) ??
    (shelvingTvProblem(project, module, "stand") ? undefined : "stand");
  if (!mount || shelvingTvProblem(project, module, mount)) return null;
  if (mount === "wall") {
    const depth = SHELVING_TV_MODELS.wall.dimensionsMm[2];
    return {
      mount,
      bottomMm: module.height + SHELVING_TV_WALL_GAP_MM,
      // Задняя плоскость телевизора — на стене за изделием.
      centerZmm: -module.depth + depth / 2,
      soundbar: null
    };
  }
  const centerZmm = -module.depth / 2;
  // Экран у модели на глубине +18 мм от центра ножек; саундбар 100 мм — сразу перед ним.
  const soundbarCenterZmm = centerZmm + 18 + 12 + SHELVING_SOUNDBAR.dimensionsMm[2] / 2;
  return {
    mount,
    bottomMm: module.height,
    centerZmm,
    soundbar: { bottomMm: module.height, centerZmm: soundbarCenterZmm }
  };
}
